import React from 'react';

// Hover overlay: a field of vertical bars whose heights are interpolated
// between several slow, independently moving bands, like a spectrum analyser.
// Only animates while the host element is hovered (plus a short fade-out),
// and is drawn behind the host's content.
//
// Props: bands (control points), lineW / gap (px), maxH (0..1), color [r,g,b],
//        active (always on, not just on hover), hoverSpeed (band speed multiplier on hover),
//        flat (all bars level, faint breathing) with flatLevel (0..1)

const FADE = 0.8;             // seconds to fade/rise in and out
const FPS_SKIP = 2;           // ~30fps

function smoothstep(t) { return t * t * (3 - 2 * t); }

class SpectrumHover extends React.Component {
  constructor(props) {
    super(props);
    this.canvasRef = React.createRef();
    this.frame = 0;
    this.fade = 0;
    this.hovered = false;
    this.t = 0;
    // Each band drifts with its own pair of slow sine rates and phases
    const BANDS = props.bands || 7;
    this.bands = Array.from({ length: BANDS }, (_, i) => ({
      r1: 0.15 + Math.random() * 0.25,
      r2: 0.05 + Math.random() * 0.12,
      p1: Math.random() * Math.PI * 2,
      p2: Math.random() * Math.PI * 2,
      base: 0.25 + Math.random() * 0.35,
      amp: 0.2 + Math.random() * 0.3
    }));
    this.onEnter = () => { this.hovered = true; this.start(); };
    this.onLeave = () => { this.hovered = false; };
    this.loop = this.loop.bind(this);
  }

  componentDidMount() {
    const host = this.canvasRef.current && this.canvasRef.current.parentElement;
    if (!host) return;
    this.host = host;
    host.addEventListener('mouseenter', this.onEnter);
    host.addEventListener('mouseleave', this.onLeave);
    if (this.props.active) this.start();
  }

  componentDidUpdate(prev) {
    // Any change of active state (re)starts the loop so it fades in or out;
    // the loop clears the canvas itself once faded out.
    if (this.props.active !== prev.active) this.start();
  }

  componentWillUnmount() {
    if (this.host) {
      this.host.removeEventListener('mouseenter', this.onEnter);
      this.host.removeEventListener('mouseleave', this.onLeave);
    }
    if (this.raf) cancelAnimationFrame(this.raf);
    // Reset so a remount (React StrictMode double-mounts in development)
    // can start a fresh loop instead of seeing a stale frame id
    this.raf = null;
    this.hovered = false;
    this.fade = 0;
    this.clear();
  }

  start() {
    if (this.raf) return;
    if (this.props.active && !this.hovered) this.fade = 1;
    this.draw();                      // first frame now, before the loop ticks
    this.last = null;
    this.raf = requestAnimationFrame(this.loop);
  }

  loop(now) {
    this.frame++;
    const dt = this.last ? Math.min(0.1, (now - this.last) / 1000) : 0.016;
    this.last = now;
    const on = this.hovered || this.props.active;
    this.fade = Math.max(0, Math.min(1, this.fade + (on ? dt : -dt) / FADE));
    // Hover runs the bands faster than the steady active state
    const speed = this.props.active ? 1 : (this.props.hoverSpeed || 3);
    this.t += dt * speed;
    if (this.frame % FPS_SKIP === 0) this.draw();
    if (this.fade > 0 || on) {
      this.raf = requestAnimationFrame(this.loop);
    } else {
      this.raf = null;
      this.clear();
    }
  }

  clear() {
    const c = this.canvasRef.current;
    if (c) c.getContext('2d').clearRect(0, 0, c.width, c.height);
  }

  draw() {
    const c = this.canvasRef.current;
    if (!c || !this.host) return;
    const w = c.clientWidth || this.host.clientWidth;
    const h = c.clientHeight || this.host.clientHeight;
    if (c.width !== w || c.height !== h) { c.width = w; c.height = h; }
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, w, h);

    const { lineW = 1, gap = 2, maxH = 0.85, color = [0, 255, 0] } = this.props;
    const BANDS = this.bands.length;
    const [cr, cg, cb] = color;

    // Band heights right now (0..1).  A flat overlay keeps every band level,
    // with only a faint uniform breathing.
    const t = this.t;
    const levels = this.props.flat
      ? this.bands.map(() => (this.props.flatLevel || 0.45) * (1 + 0.04 * Math.sin(t * 0.8)))
      : this.bands.map(b =>
        Math.max(0.05, Math.min(1, b.base + b.amp * (0.6 * Math.sin(t * b.r1 + b.p1) + 0.4 * Math.sin(t * b.r2 + b.p2))))
      );

    // Bars fade in and rise together: alpha and height both follow the eased fade
    const alpha = smoothstep(this.fade);
    const rise = alpha;
    const step = lineW + gap;
    const n = Math.max(1, Math.floor((w + gap) / step));
    for (let i = 0; i < n; i++) {
      // Interpolate smoothly between neighbouring bands for this bar
      const u = (i / Math.max(1, n - 1)) * (BANDS - 1);
      const k = Math.min(BANDS - 2, Math.floor(u));
      const f = smoothstep(u - k);
      const level = levels[k] * (1 - f) + levels[k + 1] * f;
      const barH = Math.round(level * h * maxH * rise);
      const x = i * step;
      // Brighter toward the top of each bar, faint at the base
      const g = ctx.createLinearGradient(0, h - barH, 0, h);
      g.addColorStop(0, `rgba(${cr}, ${cg}, ${cb}, ${(this.props.active ? 0.95 : 0.6) * alpha})`);
      g.addColorStop(1, `rgba(${cr}, ${cg}, ${cb}, ${0.08 * alpha})`);
      ctx.fillStyle = g;
      ctx.fillRect(x, h - barH, lineW, barH);
    }
  }

  render() {
    return <canvas ref={this.canvasRef} className="spectrum-hover" aria-hidden="true" />;
  }
}

export default SpectrumHover;
